'use client'

import { createContext, useContext, ReactNode } from 'react'
import { getAuthUrl } from '../oauth'

import trackError from '@helpers/trackError'
import { getTokensStorageLocation, useAuthTokens } from './useAuthTokens'

interface LoginContextType {
  isLoggedIn: boolean
  login: () => Promise<void>
}

const LoginContext = createContext<LoginContextType | undefined>(undefined)

interface LoginProviderProps {
  children: ReactNode
}

export function LoginProvider({ children }: LoginProviderProps) {
  const { token } = useAuthTokens()

  const login = async () => {
    const accessToken = localStorage.getItem(getTokensStorageLocation())

    if (accessToken) {
      trackError(new Error('Calling login when access token already exists'), {
        accessToken,
        section: 'Account Page'
      })
    }

    const authUrl = await getAuthUrl()

    if (authUrl) {
      window.location.href = authUrl
    }
  }

  return (
    <LoginContext.Provider
      value={{
        login,
        isLoggedIn: !!token
      }}
    >
      {children}
    </LoginContext.Provider>
  )
}

export function useLogin(): LoginContextType {
  const context = useContext(LoginContext)
  if (context === undefined) {
    throw new Error('useLogin must be used within a LoginProvider')
  }
  return context
}
